import { useCallback, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import { DateTime } from 'luxon';

import { CreditSubmarketProps } from '@graphql/query/operationLimits/credit/counterpartList';

import { DateYearMonthProps } from '~/theme/components/MonthRange';

export const SUBMARKET_LABEL_MAP: Record<string, CreditSubmarketProps> = {
  'SE/CO': 'SE',
  Sul: 'S',
  Nordeste: 'NE',
  Norte: 'N',
};

export const SUBMARKET_OPTIONS = ['Todos', 'SE/CO', 'Sul', 'Nordeste', 'Norte'];
export const COMPETENCE_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

interface CompetenceRangeValueProps {
  startCompetence: string;
  endCompetence: string;
}

export interface OperationLimitsCompetenceQueryFiltersProps {
  submarket?: CreditSubmarketProps;
  startCompetence?: string;
  endCompetence?: string;
}

export interface UseOperationLimitsCompetenceFiltersReturnProps {
  submarket: string;
  submarketOptions: string[];
  monthRangeDefaultValues: DateYearMonthProps;
  monthRangeValues: DateYearMonthProps;
  monthRangeKey: string;
  queryFilters: OperationLimitsCompetenceQueryFiltersProps;
  handleSubmarketChange: (value: string | number) => void;
  handleCompetenceRangeChange: (value: DateYearMonthProps) => void;
}

const isBusinessDay = (date: DateTime) => date.weekday <= 5;

const nextBusinessDay = (date: DateTime) => {
  let value = date.plus({ days: 1 });

  while (!isBusinessDay(value)) {
    value = value.plus({ days: 1 });
  }

  return value;
};

const toYearMonthRange = (startDate: DateTime, endDate: DateTime): DateYearMonthProps => ({
  startYear: startDate.year,
  startMonth: startDate.month,
  endYear: endDate.year,
  endMonth: endDate.month,
});

const isYearMonthComplete = (value: DateYearMonthProps) =>
  Boolean(value.startYear && value.startMonth && value.endYear && value.endMonth);

export const buildCompetence = (year?: number, month?: number) => {
  if (!year || !month) {
    return undefined;
  }

  return DateTime.fromObject({ year, month }).toFormat('yyyy-MM');
};

export const parseCompetence = (value?: string) => {
  if (!value || !COMPETENCE_PATTERN.test(value)) {
    return undefined;
  }

  const parsed = DateTime.fromFormat(value, 'yyyy-MM');

  if (!parsed.isValid) {
    return undefined;
  }

  return parsed;
};

const parseCompetenceRange = (
  startCompetence?: string | null,
  endCompetence?: string | null,
): CompetenceRangeValueProps | undefined => {
  const start = parseCompetence(startCompetence || undefined);
  const end = parseCompetence(endCompetence || undefined);

  if (!start || !end) {
    return undefined;
  }

  if (start > end) {
    return undefined;
  }

  return {
    startCompetence: start.toFormat('yyyy-MM'),
    endCompetence: end.toFormat('yyyy-MM'),
  };
};

export const calculateDefaultCompetenceRange = (referenceDate = DateTime.now()) => {
  let sixthBusinessDay = referenceDate.startOf('month').minus({ days: 1 });

  for (let businessDayIndex = 0; businessDayIndex < 6; businessDayIndex += 1) {
    sixthBusinessDay = nextBusinessDay(sixthBusinessDay);
  }

  const currentDate = referenceDate.startOf('day');
  const startDate =
    currentDate <= sixthBusinessDay.startOf('day')
      ? referenceDate.startOf('month').minus({ months: 1 })
      : referenceDate.startOf('month');
  const endDate = startDate.plus({ months: 59 });

  return toYearMonthRange(startDate, endDate);
};

const parseRangeValue = (value: DateYearMonthProps): CompetenceRangeValueProps | undefined => {
  if (!isYearMonthComplete(value)) {
    return undefined;
  }

  const startCompetence = buildCompetence(value.startYear, value.startMonth);
  const endCompetence = buildCompetence(value.endYear, value.endMonth);

  return parseCompetenceRange(startCompetence, endCompetence);
};

function useOperationLimitsCompetenceFilters(): UseOperationLimitsCompetenceFiltersReturnProps {
  const [searchParams, setSearchParams] = useSearchParams();

  const submarket = searchParams.get('submarket') || 'Todos';
  const explicitRange = parseCompetenceRange(searchParams.get('startCompetence'), searchParams.get('endCompetence'));
  const monthRangeDefaultValues = useMemo(() => calculateDefaultCompetenceRange(), []);

  const monthRangeValues = useMemo(() => {
    if (explicitRange) {
      return toYearMonthRange(
        DateTime.fromFormat(explicitRange.startCompetence, 'yyyy-MM'),
        DateTime.fromFormat(explicitRange.endCompetence, 'yyyy-MM'),
      );
    }

    return monthRangeDefaultValues;
  }, [explicitRange, monthRangeDefaultValues]);

  const queryFilters = useMemo<OperationLimitsCompetenceQueryFiltersProps>(() => {
    const mappedSubmarket = submarket === 'Todos' ? undefined : SUBMARKET_LABEL_MAP[submarket];

    return {
      submarket: mappedSubmarket,
      startCompetence: explicitRange?.startCompetence,
      endCompetence: explicitRange?.endCompetence,
    };
  }, [explicitRange, submarket]);

  const handleSubmarketChange = useCallback(
    (value: string | number) => {
      const selectedValue = String(value);
      const nextSearchParams = new URLSearchParams(searchParams);

      if (selectedValue === 'Todos') {
        nextSearchParams.delete('submarket');
      } else {
        nextSearchParams.set('submarket', selectedValue);
      }

      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleCompetenceRangeChange = useCallback(
    (value: DateYearMonthProps) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      const parsedRange = parseRangeValue(value);

      if (!parsedRange) {
        nextSearchParams.delete('startCompetence');
        nextSearchParams.delete('endCompetence');
      } else {
        nextSearchParams.set('startCompetence', parsedRange.startCompetence);
        nextSearchParams.set('endCompetence', parsedRange.endCompetence);
      }

      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const monthRangeKey = [
    monthRangeValues.startYear,
    monthRangeValues.startMonth,
    monthRangeValues.endYear,
    monthRangeValues.endMonth,
  ].join('-');

  return {
    submarket,
    submarketOptions: SUBMARKET_OPTIONS,
    monthRangeDefaultValues,
    monthRangeValues,
    monthRangeKey,
    queryFilters,
    handleSubmarketChange,
    handleCompetenceRangeChange,
  };
}

export default useOperationLimitsCompetenceFilters;
